<i18n src="./resources/locales.json"></i18n>
<template>
  <div class="ecs-interaction-footer surface lighten-1" :class="{ 'add-shadow': hasShadowClass, 'open-footer': open }">
    <div id="footer_controls">
      <div v-if="!open" class="interaction-content-tabs surface lighten-1">
        <v-item-group v-model="selectedInteractionContentTab" mandatory class="text-center">
          <v-item v-for="tab in interactionContentTabs" :key="tab.title" v-slot:default="{ active, toggle }">
            <v-tooltip bottom z-index="120" :content-class="'tooltip-' + tab.title.toLowerCase()">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-show="tab.visible"
                  :input-value="active"
                  :ripple="false"
                  :disabled="tab.disabled"
                  active-class="interaction-active-content-tab"
                  class="interaction-content-tab"
                  v-bind="attrs"
                  icon
                  small
                  v-on="on"
                  @click="toggle"
                >
                  <v-icon>{{ tab.icon }}</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(tab.i18nKey) }}</span>
            </v-tooltip>
          </v-item>
        </v-item-group>
      </div>

      <div v-if="!open && !interaction.isTelephony" class="interaction-content-tabs surface lighten-1">
        <v-tooltip
          bottom
          z-index="120"
          :content-class="!isCrmNavigationEnabled ? 'tooltip-download bottom-right' : 'tooltip-download '"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :ripple="false"
              class="interaction-content-tab"
              v-bind="attrs"
              icon
              small
              v-on="on"
              @click="downloadChat({ interaction, agent: loggedInAgent.name, emailContent })"
            >
              <v-icon>{{ 'mdi-download' }}</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('tabs.downloadChat', { mediaType }) }}</span>
        </v-tooltip>
      </div>
      <div>
        <v-btn v-show="showAgentControls" class="actions-btn" text @click="toggleAgentActionsVisibility(interaction)">
          <span v-once>{{ $t('buttons.agentActions') }}</span>
          <v-icon v-if="open" small>mdi-chevron-down</v-icon>
          <v-icon v-else small>mdi-chevron-up</v-icon>
        </v-btn>
      </div>
    </div>
    <div id="footer_form" class="custom-scrollbar" :class="{ 'open-form': open }">
      <EcsCustomInput
        v-if="showAgentControls"
        ref="dispositionCodeTree"
        v-model="selectedDispositionCode"
        class="custom-input"
        type="treeSelect"
        :clearable="!interaction.isHistoric"
        :disabled="isDispositionCodesDisabled"
        :items="dispositionCodeList"
        :label="$t('labels.dispositionCode')"
        :placeholder="$t('placeholders.dispositionCode')"
        :required="interaction.disposition.dispositionCodeMandatory"
        :rules="dispositionCodeRules"
        disable-branch-nodes
        searchable
      >
        <template v-slot:value-label="{ node }">
          {{ getDispositionCodePath(node) }}
        </template>
      </EcsCustomInput>

      <EcsCustomInput
        v-if="showAgentControls"
        ref="completionStatusSelect"
        v-model="selectedCompletionStatusId"
        class="custom-input"
        type="select"
        item-text="name"
        item-value="value"
        :disabled="isCompletionStatusDisabled"
        :items="completionStatuses"
        :label="$t('labels.completionStatus')"
        :placeholder="$t('placeholders.completionStatus')"
      />

      <EcsCustomInput
        v-if="isDelegateToSupervisorSelected"
        ref="delegateInput"
        v-model="delegateToSvRemark"
        class="custom-input"
        type="text"
        :clearable="!interaction.isHistoric"
        :disabled="interaction.isHistoric"
        :label="$t('labels.delegateNotes')"
        :placeholder="$t('placeholders.delegateNotes')"
      />

      <EcsInteractionRemarks
        v-if="openOnce && !interaction.isInternal && !interaction.isDirectCall"
        ref="remarks"
        :allow-new-remarks="!interaction.isHistoric"
        :interaction-id="interaction.interactionId"
        :contact-id="interaction.crm.contactId"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import EcsInteractionMixin from '@/mixins/ecs-interaction-mixin/EcsInteractionMixin';
import { DELEGATE_TO_SUPERVISOR, CALLBACK_REQUESTED } from '@/constants/appConstants';
import { PENDING_STATUS } from '@/constants/interaction/constants';
import { NOT_SELECTED_TIMEOUT_DISPOSITION_CODE_ID } from '@/constants/socket-events';
import { findDispositionCodeById, getPathFromTreeByNodeId, traverseDispositionCodesObject } from '@/utils';
import EcsInteractionRemarks from '@/components/ecs-interaction-remarks/EcsInteractionRemarks';
import InteractionService from '@/services/interactionService';

export default {
  name: 'InteractionFooter',

  components: { EcsInteractionRemarks },

  mixins: [EcsInteractionMixin],

  props: {
    interaction: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      delegateToSvRemark: '',
      open: false,
      openOnce: false,
      remarkText: '',
      rules: {
        required: value => (value !== null && value !== undefined) || '* Required'
      },
      selectedCompletionStatusId: null,
      selectedDispositionCode: null,
      selectedInteractionContentTab: 0
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('CONTACTS', ['getCrmContact']),

    ...mapGetters('INTERACTIONS/dispositionCodes', ['mostUsedDispositionCodes']),

    ...mapGetters('INTERACTIONS/emails', ['getEmailByInteractionId']),

    ...mapGetters('ORGANIZATION', ['dispositionCodeCategorySetPerBp', 'getInteractionDetailsDisplayTemplateForBP']),

    interactionDetailTemplate() {
      return this.getInteractionDetailsDisplayTemplateForBP(this.interaction.routing.businessProcessId);
    },

    dispositionCodeCategorySet() {
      const set = this.dispositionCodeCategorySetPerBp(this.interaction.routing.businessProcessId);
      const dispositionCodes = {};

      if (!set) {
        return [];
      }

      set.categories.forEach(category => {
        dispositionCodes[category.name] = {};
        category.subCategories.forEach(subCategory => {
          dispositionCodes[category.name][subCategory.name] = subCategory.dispositionCodes;
        });
      });

      return traverseDispositionCodesObject(dispositionCodes);
    },

    isCrmNavigationEnabled() {
      if (this.interaction.isOutgoingEmail || this.interaction.isConsultReceiver) {
        return false;
      }

      return (
        this.loggedInAgent.crm === 'Enabled' &&
        (this.interaction.isExternalCall
          ? !!this.getCrmContact(this.interaction.crm.contactId)
          : !this.interaction.isInternal)
      );
    },

    interactionContentTabs() {
      return [
        {
          icon: this.getInteractionIcon(),
          title: 'Interaction',
          i18nKey: 'tabs.interaction',
          visible: !this.interaction.isHistoric || (this.interaction.customer && this.interaction.customer.id)
        },
        {
          icon: 'mdi-format-list-bulleted',
          title: 'Contact',
          i18nKey: 'tabs.contact',
          visible:
            !this.interaction.isDirectCall &&
            this.interaction.customer &&
            this.interaction.customer.id &&
            this.isCrmNavigationEnabled
        },
        {
          icon: 'mdi-history',
          title: 'History',
          i18nKey: 'tabs.history',
          visible: this.interaction.customer && this.interaction.customer.id && this.isCrmNavigationEnabled
        },
        {
          icon: 'mdi-clipboard-text',
          title: 'Notes',
          i18nKey: 'tabs.notes',
          visible: this.interaction.customer && this.interaction.customer.id && this.isCrmNavigationEnabled
        },
        {
          icon: 'mdi-voicemail',
          title: 'VoiceMail',
          i18nKey: 'tabs.voiceMail',
          visible:
            this.interaction.isTelephony && this.interaction.callbackRecordingFile && this.isCrmNavigationEnabled,
          disabled: false
        },
        {
          icon: 'mdi-dots-horizontal',
          title: 'InteractionDetail',
          i18nKey: 'tabs.interactionDetail',
          visible: !this.interaction.isHistoric && !!this.interactionDetailTemplate
        }
      ];
    },

    completionStatuses() {
      // Note: Filtering out callback requested, since it causes problems when selected
      const completionStatusesList = structuredClone(this.interaction.completionStatuses || []);
      completionStatusesList
        .filter(item => item.value !== CALLBACK_REQUESTED)
        .map(item => {
          item.name = item.name.replace(/([A-Z])/g, ' $1').trim();
          return item;
        });

      return completionStatusesList;
    },

    dispositionCodeList() {
      // Filter only most used disposition codes that are enabled for the interaction
      const mostUsedList = this.mostUsedDispositionCodes
        .filter(code => !!findDispositionCodeById(this.dispositionCodeCategorySet, code.id))
        .slice(0, 3);

      const mostUsedNode = {
        id: `most-used-codes`,
        label: 'Most Used',
        isDisabled: !mostUsedList.length,
        children: mostUsedList.map(code => {
          const path = getPathFromTreeByNodeId(this.dispositionCodeCategorySet, code.id);
          return {
            id: `most-used-${code.id}`,
            label: path,
            path // Overwrite path to avoid showing 'Most Used' in the path
          };
        })
      };

      const result = [mostUsedNode].concat(this.dispositionCodeCategorySet);

      /*
        NOTE: This is a workaround to include the disposition code by default
        when mandatory and no value was provided until the interaction is ended
        automatically by ECS. This disposition code is not selectable and is not returned
        in the API so it's hard-coded by now in our end
      */
      if (this.interaction.isHistoric) {
        result.push({
          id: NOT_SELECTED_TIMEOUT_DISPOSITION_CODE_ID,
          label: 'Not Selected Timeout'
        });
      }

      return result;
    },

    isDelegateToSupervisorSelected() {
      return this.selectedCompletionStatusId === DELEGATE_TO_SUPERVISOR;
    },

    isDispositionCodesDisabled() {
      return this.interaction.isHistoric || this.dispositionCodeList.length === 0;
    },

    showAgentControls() {
      return (
        !this.interaction.isDirectCall &&
        !this.interaction.isConsultReceiver &&
        !this.isDestinationOnInternalInteraction
      );
    },

    hasShadowClass() {
      return !this.interaction.isChat && !this.interaction.isSms;
    },

    isCompletionStatusDisabled() {
      return this.interaction.isHistoric || this.completionStatuses.length === 1;
    },

    dispositionCodeRules() {
      return this.interaction.disposition.dispositionCodeMandatory ? [this.rules.required] : [];
    },

    emailContent() {
      if (!this.interaction.isOutgoingEmail && !this.interaction.isIncomingEmail) {
        return false;
      }
      return this.getEmailByInteractionId(this.interaction.interactionId);
    },

    mediaType() {
      if (this.interaction.isChat) return 'Chat';
      else if (this.interaction.isSms) return 'Sms';
      else if (this.interaction.isEmail) return 'Email';
      else return 'Interaction';
    }
  },

  watch: {
    interaction: {
      handler(updatedInteraction) {
        this.selectedDispositionCode = updatedInteraction.disposition.dispositionCodeId;
        this.validate();

        if (updatedInteraction.isOnWrapUp) {
          this.open = true;
          this.openOnce = true;
          this.$root.$emit('close-dialpad');
        }
      },
      deep: true
    },

    selectedInteractionContentTab(newValue) {
      this.$parent.$emit('select-interaction-content-tab', newValue);
    },

    selectedDispositionCode(newValue) {
      // NOTE: Workaround to solve the bad practices in the ECS RESTful API services :(
      this.updateInteractionDispositionCode({
        interactionId: this.interaction.interactionId,
        dispositionCodeId: newValue
      });

      if (!this.interaction.isHistoric) {
        this.saveDispositionCode(newValue);
      }
    },

    selectedCompletionStatusId(completionStatusId) {
      // WORKAROUND NOTE: This will save the selected completion status data
      // into vuex storage since ECS events or API (interaction/all) don't return these
      const completionStatus = this.interaction.completionStatuses.find(cs => cs.value === completionStatusId);

      if (!completionStatus) {
        return;
      }

      const { name: completionStatusName } = completionStatus;
      let completionStatusInformation = null;
      if (this.isDelegateToSupervisorSelected) {
        completionStatusInformation = {
          delegateToSvRemark: this.delegateToSvRemark || '',
          pendingStatus: PENDING_STATUS.delegateToSupervisor
        };
      }

      this.updateInteractionCompletionStatus({
        interactionId: this.interaction.interactionId,
        completionStatus: {
          completionStatusId,
          completionStatusName,
          completionStatusInformation
        }
      });
    },

    delegateToSvRemark(delegateToSvRemark) {
      // WORKAROUND NOTE: this will save the selected delegateToSvRemark
      // since ECS events are not returning this.

      let completionStatusInformation = null;
      if (delegateToSvRemark) {
        completionStatusInformation = {
          delegateToSvRemark,
          pendingStatus: PENDING_STATUS.delegateToSupervisor
        };
      }

      this.updateInteractionCompletionStatusInformation({
        interactionId: this.interaction.interactionId,
        completionStatusInformation
      });
    }
  },

  mounted() {
    if (this.interaction.isOnWrapUp) {
      this.open = true;
      this.openOnce = true;
    }

    const {
      completion: { completionStatusInformation }
    } = this.interaction;

    let dispositionCodeId = this.interaction.disposition.dispositionCodeId;
    if (!dispositionCodeId && this.interaction.disposition.dispositionCodeName) {
      const dispositionCode = this.dispositionCodeCategorySet
        .flatMap(category => category.children)
        .flatMap(subCategory => subCategory.children)
        .find(a => a.label === this.interaction.disposition.dispositionCodeName);
      dispositionCodeId = dispositionCode ? dispositionCode.id : null;
    }

    this.selectedDispositionCode = dispositionCodeId;
    const defaultHandledCompletionStatus = this.completionStatuses.find(
      completionStatus => completionStatus.name === 'Handled'
    );
    this.selectedCompletionStatusId = defaultHandledCompletionStatus ? defaultHandledCompletionStatus.value : null;
    this.delegateToSvRemark = completionStatusInformation ? completionStatusInformation.delegateToSvRemark : '';

    this.validate();
  },

  methods: {
    ...mapActions('INTERACTIONS', [
      'updateInteractionCompletionStatus',
      'updateInteractionDispositionCode',
      'updateInteractionCompletionStatusInformation'
    ]),

    ...mapActions('CONVERSATIONS', ['downloadChat']),

    ...mapActions('INTERACTIONS/dispositionCodes', ['updateMostUsedDispositionCodes']),

    getDispositionCodePath(code) {
      return code.raw.path || getPathFromTreeByNodeId(this.dispositionCodeList, code.raw.id);
    },

    toggleAgentActionsVisibility() {
      this.open = !this.open;
      this.openOnce = true;
      this.$root.$emit(`agent-actions-toggled-${this.interaction.interactionId}`);

      if (this.$refs.remarks) {
        this.$refs.remarks.hideRemarkInput();
      }
    },

    saveDispositionCode(dispositionCodeId) {
      if (!dispositionCodeId || dispositionCodeId === NOT_SELECTED_TIMEOUT_DISPOSITION_CODE_ID) {
        return;
      }

      // NOTE: If selected from most-used list then it has the prefix most-used
      // that we need to remove. The prefix was added to avoid warning due to
      // duplicated ids in the vue-treeselect component
      dispositionCodeId = dispositionCodeId.replace('most-used-', '');

      InteractionService.updateDispositionCode(this.interaction.interactionId, dispositionCodeId)
        .then(() => {
          const dispositionCode = findDispositionCodeById(this.dispositionCodeList, dispositionCodeId);
          if (dispositionCode) {
            this.updateMostUsedDispositionCodes(dispositionCode);
          }
        })
        .catch(console.error);
    },

    validate() {
      let valid = true;
      Object.values(this.$refs).forEach(input => {
        if (input && typeof input.validate === 'function') {
          if (!input.validate()) {
            valid = false;
          }
        }
      });

      return valid;
    },
    getInteractionIcon() {
      if (this.interaction.isChat) {
        return 'mdi-message-text-outline';
      } else if (this.interaction.isTelephony) {
        return 'mdi-phone';
      }
      return this.interaction.isSms ? 'mdi-chat-processing-outline' : 'mdi-email';
    }
  }
};
</script>

<style scoped>
.add-shadow {
  box-shadow: var(--v-inset-shadow);
}
.ecs-interaction-footer {
  position: absolute;
  bottom: 0;
  right: 0;
  height: 36px;
  width: 100% !important;
  border-radius: 0 0 10px 10px;
  transition: all 250ms ease-out;
}

.open-footer {
  height: calc(100% - 164px) !important;
  z-index: 2;
  box-shadow: none;
}

#footer_controls {
  height: 36px;
  display: flex;
}

#footer_form {
  overflow: hidden;
  height: 0;
}

#footer_form .custom-input:not(:last-child) {
  margin-bottom: 16px;
}

.actions-btn {
  position: absolute;
  right: 0;
  margin: 0;
  text-transform: capitalize;
}

.open-form {
  display: block !important;
  padding: 0 16px;
  height: calc(100% - 52px) !important;
  overflow: auto !important;
}

.interaction-content-tabs {
  align-self: center;
  margin-left: 8px;
}

.interaction-content-tab:not(:first-child) {
  margin-left: 8px;
}

.interaction-content-tab .v-icon {
  font-size: 12px;
  transition: all 250ms;
}

.interaction-content-tab.v-btn:before {
  background-color: transparent;
}

.interaction-content-tab:not(.v-btn--active):hover {
  background-color: var(--v-surface-lighten2);
}

.interaction-content-tab.v-btn--active:before {
  background-color: transparent;
  opacity: 1;
}

.interaction-active-content-tab {
  color: var(--v-primary-base) !important;
}

.interaction-active-content-tab .v-icon {
  font-size: 24px !important;
  transition: all 250ms;
}

.tooltip-interaction {
  margin-left: 63.5px;
}

.tooltip-download.bottom-right {
  margin-left: 43.5px;
}
</style>
